/* eslint-disable sonarjs/no-duplicate-string, camelcase */
import { TGTMInfo } from 'types/models/product';
import eventBus from 'widgets/toolbox/eventBus';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { get } from 'widgets/toolbox/util';
import { isMediumViewAndDown } from 'widgets/toolbox/viewtype';
import { trackProductClick, getTilePosition, pageProperties, report, getProductList, trackCart } from '../utils/base';
import {
    impressedProducts,
    impressionsDebounce,
    addToImpressedProducts,
    reportImpression,
    TImpression
} from './impressions';

declare global {
    interface Window {
        analyticsPageData: {
            type: string;
            locale: string;
            id: string;
        };
    }
}

type TAddToCartData = {
    ecommerce: {
        currency: string;
        checkout_type?: string;
        value: string;
        feature: string;
        items: Array<TGTMInfo>;
        bopis?: string;
    };
};

/**
 * @description GTM events
 * @module events
 * @category widgets
 * @subcategory int_gtm
 */

type TProductEcommerce = {
    currency?: unknown;
    value?: unknown;
    items?: Array<Record<string, unknown>>;
};

let currentProductEcommerce: TProductEcommerce | null = null;

function getProductEcommerce(): TProductEcommerce {
    if (currentProductEcommerce) {
        return currentProductEcommerce;
    }

    const dl = window.dataLayer as unknown as Array<Record<string, unknown>>;

    if (Array.isArray(dl)) {
        for (let i = dl.length - 1; i >= 0; i--) {
            const entry = dl[i];

            if (entry?.event === 'view_item') {
                const ecommerce = entry.ecommerce as TProductEcommerce;

                if (ecommerce?.items?.length) {
                    currentProductEcommerce = {
                        currency: ecommerce.currency,
                        value: ecommerce.value,
                        items: ecommerce.items
                    };

                    return currentProductEcommerce;
                }
            }
        }
    }

    return {};
}

type TGalleryProduct = {
    images?: Record<string, Array<unknown>>;
    elevationGallerySlides?: Array<{ slideType?: string }>;
};

/**
 * @description Counts the gallery images for the active variant (video slides excluded).
 * @param product - updated product model
 * @param isElevation - whether the elevation gallery is active
 * @returns number of gallery images
 */
function getGalleryImagesTotal(product: TGalleryProduct, isElevation: boolean): number {
    if (isElevation && Array.isArray(product.elevationGallerySlides)) {
        return product.elevationGallerySlides.filter((slide) => slide.slideType !== 'video').length;
    }

    const images = product.images || {};
    const list = images.large || images.zoom || images.small || [];

    return Array.isArray(list) ? list.length : 0;
}

/**
 * @description Re-fires the passive `page_load` gallery interaction for the active variant's first
 * image. Initial page load is handled server-side; this covers client-side color switches.
 * @param product - updated product model from the variation response
 * @param ecommerce - product ecommerce payload
 */
function reportGalleryPageLoad(product: TGalleryProduct, ecommerce: TProductEcommerce | null): void {
    if (!window.gtmGenericData?.pdpGalleryTrackingEnabled) {
        return;
    }

    // Gallery interactions are mobile-only, so the baseline must not fire on desktop.
    if (!isMediumViewAndDown()) {
        return;
    }

    // The gallery is reused in QuickView (cart edit / wishlist); only the PDP gallery should track it.
    const galleryEl = document.querySelector('[data-elevation-gallery][data-quick-view="false"]') as HTMLElement | null;

    if (!galleryEl) {
        return;
    }

    const isElevation = galleryEl.dataset?.elevationGallery === 'true';
    const galleryImagesTotal = getGalleryImagesTotal(product, isElevation);

    if (galleryImagesTotal <= 0) {
        return;
    }

    report('pdp_gallery_interaction', {
        gallery_type: isElevation ? 'elevation' : 'regular',
        gallery_interaction_type: 'page_load',
        gallery_image_index: 1,
        gallery_images_total: galleryImagesTotal,
        ecommerce
    }, true);
}

/**
 * @description Init GTM events
 */
export function init(): void {
    /**
     * @description Update global GTM data
     * @event "gtm.update"
     * @listens "gtm.update"
     */
    eventBus.on('gtm.update', (analytics, options = {}) => {
        if (!analytics) {
            return;
        }

        ['analyticsPageData', 'gtmUserData'].forEach(key => {
            const data = analytics[key];

            if (data && typeof data === 'object') {
                if (options.extend) {
                    window[key] = { ...get(window, key, {}), ...data };
                } else {
                    window[key] = data;
                }
            }
        });

        if (options.reportPageMeta) {
            report('page_meta', {
                page: window.analyticsPageData,
                user: window.gtmUserData,
                salesforce_experiments: window.gtmSalesforceExperiments || []
            });
        }
    });
    /**
     * @description Tracks in dataLayer an information about any global event
     * @event "gtm.event"
     * @listens "gtm.event"
     */
    eventBus.on('gtm.event', analytics => {
        if (!analytics) {
            return;
        }

        report('ga4_event', analytics);
    });

    /**
     * @description Tracks in dataLayer an information about any global event
     * @event "gtm.event"
     * @listens "gtm.event"
     */
    eventBus.on('impressions.reset', () => {
        window.gtmImpressions = {
            event: 'pageLoad',
            ecommerce: {
                currencyCode: window.gtmImpressions?.ecommerce?.currencyCode || '',
                impressions: []
            }
        };

        impressedProducts.length = 0;
    });

    /**
     * @description Tracks in dataLayer an information about product impressions on product tile init
     * @event "product.tile.init"
     * @listens "product.tile.init"
     */
    eventBus.on('product.tile.init', (tile: TRefElementInstance) => {
        const gtmInfoData = tile.data<TGTMInfo>('analytics');
        const carouselView = tile.data('carouselView');

        if (!gtmInfoData || carouselView) {
            return;
        }

        const position = getTilePosition(gtmInfoData, { shouldBeNotImpressed: true });

        if (position) {
            const impression: TImpression = {
                ...gtmInfoData,
                item_list_id: getProductList(tile),
                index: position
            };

            addToImpressedProducts(impression);

            impressionsDebounce.push(impression);

            reportImpression();
        }
    });

    /**
     * @description Tracks in dataLayer an information about quick view opening
     * @event "product.tile.qv.open"
     * @listens "product.tile.qv.open"
     */
    eventBus.on('product.tile.qv.open', qvData => {
        report('quick_view_item', {
            item_list_id: 'Product quick view',
            currency: pageProperties.currentCurrency,
            value: qvData.gtmInfo.price,
            items: [qvData.gtmInfo]
        });
    });

    /**
     * @description Tracks in dataLayer an information about updated variant.
     * Color switches (cause: "color_switch") keep firing `view_item` so GA4 product
     * view metrics stay accurate. Size and width selections (cause: "size_selection" /
     * "width_selection") fire a dedicated `select_product_option` event instead, so they
     * no longer inflate `view_item` counts, while marketing pixels keep receiving the
     * exact same items[] / item_meta payload they rely on today.
     * @event "product.variant.updated"
     * @listens "product.updated"
     */
    eventBus.on('product.updated', (response, cause?: string) => {
        if (!response?.product?.gtmInfo) {
            return;
        }

        const isProductOptionSelection = cause === 'size_selection' || cause === 'width_selection';
        const eventName = isProductOptionSelection ? 'select_product_option' : 'view_item';

        const ecommerce: Record<string, unknown> = {
            currency: pageProperties.currentCurrency,
            value: response.product.gtmInfo.price,
            items: [
                {
                    ...response.product.gtmInfo,
                    item_list_id: 'Product item view',
                    // A single product on a PDP is always position 1 in its list.
                    index: 1
                }
            ]
        };

        currentProductEcommerce = {
            currency: ecommerce.currency,
            value: ecommerce.value,
            items: ecommerce.items as Array<Record<string, unknown>>
        };

        if (cause) {
            ecommerce.cause = cause;
        }

        report(eventName, { ecommerce });

        // A color switch resets the gallery to the new variant's first image — re-fire the baseline.
        if (cause === 'color_switch') {
            reportGalleryPageLoad(response.product, currentProductEcommerce);
        }
    });

    /**
     * @description Tracks in dataLayer an information about quick view opening on editing product step
     * (Cart Edit Widget / Wishlist Page). Treated as an initial widget load, so it keeps firing `view_item`.
     * @event "product.tile.qv.open.edit"
     * @listens "product.tile.qv.open.edit"
     */
    eventBus.on('product.tile.qv.open.edit', editEl => {
        if (!editEl) {
            return;
        }

        const productInfo = editEl.data('analytics');

        if (!productInfo) {
            return;
        }

        report('view_item', {
            ecommerce: {
                cause: 'load',
                currency: pageProperties.currentCurrency,
                value: productInfo.price,
                items: [
                    {
                        ...productInfo,
                        item_list_id: 'Product quick view'
                    }
                ]
            }
        });
    });
    /**
     * @description Tracks in dataLayer an information about ctaExt button click
     * @event "ctaExt.click"
     * @listens "ctaExt.click"
     * @returns {void}
     */
    eventBus.on('ctaExt.click', elem => {
        report('cta_ext_click', {
            targetUrl: elem.attr('href'),
            title: elem.prop('innerText') ?? elem.prop('title')
        });
    });
    /**
     * @description Tracks in dataLayer an information about video-banner action event
     * @event "videoPoster.click"
     * @listens "videoPoster.click"
     * @returns {void}
     */
    eventBus.on('videoPoster.click', (playerType, videoName) => {
        report('video_poster_click', {
            playerType: playerType,
            name: videoName
        });
    });
    /**
     * @description Tracks in dataLayer an information about video action event
     * @event "hotSpot.click"
     * @listens "hotSpot.click"
     * @returns {void}
     */
    eventBus.on('hotSpot.click', titleText => {
        report('hot_spot_click', {
            title: titleText
        });
    });
    /**
     * @description Tracks in dataLayer an information product carousel action event
     * @event "simpleCarousel.click"
     * @listens "simpleCarousel.click"
     * @returns {void}
     */
    eventBus.on('simpleCarousel.click', (actionType, slideData) => {
        report('simple_carousel_click', {
            item: slideData,
            action: actionType
        });
    });
    /**
     * @description Tracks in dataLayer an information about product tile link click
     * @event "trackLink.clicked"
     * @listens "trackLink.clicked"
     */
    eventBus.on('trackLink.clicked', el => {
        if (!el) {
            return;
        }

        const linkUrl = el.attr('href');

        report('ga4_event', {
            event_name: 'click_link',
            click_link: {
                feature: 'promo_banner',
                target: linkUrl
            }
        });
    });
    /**
     * @description Tracks in dataLayer an information hero carousel action event
     * @event "heroCarousel.click"
     * @listens "heroCarousel.click"
     * @returns {void}
     */
    eventBus.on('heroCarousel.click', (actionType, slideData) => {
        report('hero_carousel_click', {
            item: slideData,
            action: actionType
        });
    });
    /**
     * @description Tracks PDP gallery interactions (thumbnail click, arrow click, swipe)
     * @event "pdp.gallery.interaction"
     * @listens "pdp.gallery.interaction"
     */
    eventBus.on('pdp.gallery.interaction', (data: {
        gallery_type: string;
        gallery_interaction_type: string;
        gallery_image_index: number;
        gallery_images_total: number;
    }) => {
        if (!window.gtmGenericData?.pdpGalleryTrackingEnabled) {
            return;
        }

        const ecommerce = getProductEcommerce();

        report('pdp_gallery_interaction', {
            gallery_type: data.gallery_type,
            gallery_interaction_type: data.gallery_interaction_type,
            gallery_image_index: data.gallery_image_index,
            gallery_images_total: data.gallery_images_total,
            ecommerce
        }, true);
    });
    /**
     * @description Tracks PDP video slide transitions (swipe, arrow_click, scrollbar_click)
     * @event "pdp.video.interaction"
     * @listens "pdp.video.interaction"
     */
    eventBus.on('pdp.video.interaction', (data: {
        gallery_type: string;
        gallery_interaction_type: string;
        gallery_video_provider: string;
        gallery_video_title: string;
        gallery_video_index: number;
        gallery_video_total: number;
    }) => {
        if (!window.gtmGenericData?.pdpVideoTrackingEnabled) {
            return;
        }

        const ecommerce = getProductEcommerce();

        report('pdp_video_interaction', {
            gallery_type: data.gallery_type,
            gallery_interaction_type: data.gallery_interaction_type,
            gallery_video_provider: data.gallery_video_provider,
            gallery_video_title: data.gallery_video_title,
            gallery_video_index: data.gallery_video_index,
            gallery_video_total: data.gallery_video_total,
            ecommerce
        }, true);
    });
    /**
     * @description Tracks in dataLayer an information about product tile link click
     * @event "tile.product.link.click"
     * @listens "tile.product.link.click"
     */
    eventBus.on('tile.product.link.click', (link, data) => {
        if (data?.carouselView) {
            return;
        }

        trackProductClick(link);
    });
    /**
     * @description Tracks in dataLayer an information about product details link click
     * @event "detail.product.link.click"
     * @listens "detail.product.link.click"
     */
    eventBus.on('detail.product.link.click', link => {
        trackProductClick(link);
    });
    /**
     * @description Tracks in dataLayer an information about searchbox product tile link click
     * @event "searchbox.product.link.click"
     * @listens "searchbox.product.link.click"
     */
    eventBus.on('searchbox.product.link.click', link => {
        trackProductClick(link);
    });
    /**
     * @description Tracks in dataLayer an information about wishlist product tile link click
     * @event "wishlist.product.link.click"
     * @listens "wishlist.product.link.click"
     */
    eventBus.on('wishlist.product.link.click', link => {
        trackProductClick(link);
    });
    /**
     * @description Tracks in dataLayer an information about comparison product tile link click
     * @event "comparison.product.link.click"
     * @listens "comparison.product.link.click"
     */
    eventBus.on('comparison.product.link.click', link => {
        trackProductClick(link);
    });
    /**
     * @description Tracks in dataLayer an information about adding products to cart
     * @event "product.added.to.cart"
     * @listens "product.added.to.cart"
     */
    eventBus.on('product.added.to.cart', (cart, widget, feature) => {
        const tileInfo = widget.currentGtmInfo || widget.data('analytics');

        if (!tileInfo) {
            return;
        }

        const buttonId = widget.data('id');
        let pageType = tileInfo.pageType;
        let qty = tileInfo.quantity || 1;

        delete tileInfo.pageType;

        if (buttonId && buttonId === 'suggestion-result') {
            pageType = 'fit_guide';
        } else if (window.analyticsPageData.type === 'checkout') {
            pageType = window.analyticsPageData.id;
        }

        if (widget.initValue && widget.currentValue) {
            const initValue = parseInt(widget.initValue, 10);
            const currQty = parseInt(widget.currentValue, 10);

            qty = currQty - initValue;
        }

        tileInfo.quantity = qty;

        const bonusTileInfo = tileInfo.combinedBonusGtmInfo || [];

        delete tileInfo.combinedBonusGtmInfo;

        const addToCartData: TAddToCartData = {
            ecommerce: {
                currency: pageProperties.currentCurrency,
                value: tileInfo.price * qty + '',
                feature: feature || pageType || window.analyticsPageData.type,
                items: [tileInfo, ...bonusTileInfo]
            }
        };

        if (Object.prototype.hasOwnProperty.call(tileInfo, 'isBOPISSelected')) {
            addToCartData.ecommerce.bopis = tileInfo.isBOPISSelected ? 'pickup' : 'shipping';
            delete tileInfo.isBOPISSelected;
        }

        report('add_to_cart', addToCartData);

        trackCart(cart, [tileInfo]);
    });
    /**
     * @description Tracks in dataLayer an information about adding product set to cart
     * @event "product.set.added.to.cart"
     * @listens "product.set.added.to.cart"
     */
    eventBus.on('product.set.added.to.cart', (cart, widget) => {
        const setInfo = widget.currentGtmInfo || widget.data('analytics');
        let items = widget.getProductSetItems();

        if (!items?.length) {
            return;
        }

        items = items.map(item => {
            return item.widget.data('analytics');
        });

        let pageType = window.analyticsPageData.type;

        if (pageType === 'checkout') {
            pageType = window.analyticsPageData.id;
        }

        report('add_to_cart', {
            ecommerce: {
                currency: pageProperties.currentCurrency,
                value: setInfo.price,
                feature: pageType,
                items: items
            }
        });

        trackCart(cart, items);
    });
    /**
     * @description Tracks in dataLayer an information about bonus products added to cart
     * @event "bonus.product.added.to.cart"
     * @listens "bonus.product.added.to.cart"
     */
    eventBus.on('bonus.product.added.to.cart', (cart, widget, feature) => {
        const tileInfo = widget.bonusGtmInfo || cart.addedBonusProductsGtmInfo;

        if (!tileInfo?.length) {
            return;
        }

        let pageType = window.analyticsPageData.type;

        if (pageType === 'checkout') {
            pageType = window.analyticsPageData.id;
        }

        const addToCartData: TAddToCartData = {
            ecommerce: {
                currency: pageProperties.currentCurrency,
                value: '0',
                feature: feature || pageType,
                items: tileInfo
            }
        };

        if (Object.prototype.hasOwnProperty.call(tileInfo, 'isBOPISSelected')) {
            addToCartData.ecommerce.bopis = tileInfo.isBOPISSelected ? 'pickup' : 'shipping';
            delete tileInfo.isBOPISSelected;
        }

        report('add_to_cart', addToCartData);

        trackCart(cart, [tileInfo]);
    });

    /**
     * @description Tracks in dataLayer an information about adding products to wishlist
     * @event "product.added.to.wishlist"
     * @listens "product.added.to.wishlist"
     */
    // eslint-disable-next-line sonarjs/cognitive-complexity
    eventBus.on('product.added.to.wishlist', widget => {
        const tileInfo = widget.currentGtmInfo || widget.data('analytics');

        if (!tileInfo) {
            return;
        }

        tileInfo.quantity ??= 1;

        let pageType = tileInfo.pageType || window.analyticsPageData.type;

        if (pageType === 'checkout') {
            pageType = window.analyticsPageData.id;
        }

        const dataContentTypeAttr = 'data-tau-unique';
        const contentTypeEl =
            widget.ref && widget.ref('self').get() && widget.ref('self').get().closest(`div[${dataContentTypeAttr}]`);

        if (contentTypeEl) {
            const dataContentType = contentTypeEl.getAttribute(dataContentTypeAttr);
            const recommendationAttrValues = ['recommendations', 'recently_viewed'];

            if (recommendationAttrValues.includes(dataContentType)) {
                pageType = 'recommendation';
            }
        }

        delete tileInfo.pageType;
        delete tileInfo.combinedBonusGtmInfo;

        report('add_to_wishlist', {
            ecommerce: {
                currency: pageProperties.currentCurrency,
                feature: pageType,
                value: tileInfo.price,
                items: [
                    {
                        ...tileInfo,
                        item_list_id: getProductList(widget)
                    }
                ]
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about updates on cart
     * @event "cart.updated"
     * @listens "cart.updated"
     */
    eventBus.on('cart.updated', cart => {
        if (cart && cart.items) {
            cart.items.forEach(item => {
                if (item.gtmInfo) {
                    item.gtmInfoStr = JSON.stringify(item.gtmInfo);
                }
            });
        }
    });
    /**
     * @description Tracks in dataLayer an information about removing products from cart
     * @event "cart.remove.product"
     * @listens "cart.remove.product"
     */
    eventBus.on('cart.remove.product', (cart, widget, feature) => {
        const tileInfo = widget.removeButton
            ? widget.removeButton.data('analytics')
            : widget.initialGtmInfo || widget.data('analytics');

        if (!tileInfo) {
            return;
        }

        const bonusTileInfo = tileInfo.combinedBonusGtmInfo || [];
        let qty = widget.removeButton ? parseInt(widget.removeButton.data('quantity'), 10) : 1;
        let pageType = tileInfo.pageType || window.analyticsPageData.type;

        if (pageType === 'checkout') {
            pageType = window.analyticsPageData.id;
        }

        if (widget.initValue && widget.currentValue) {
            const initValue = parseInt(widget.initValue, 10);
            const currQty = parseInt(widget.currentValue, 10);

            qty = initValue - currQty;
        }

        tileInfo.quantity = qty;

        delete tileInfo.list;
        delete tileInfo.combinedBonusGtmInfo;
        delete tileInfo.pageType;

        report('remove_from_cart', {
            ecommerce: {
                currency: pageProperties.currentCurrency,
                value: tileInfo.price * qty + '',
                feature: feature || pageType,
                items: [tileInfo].concat(bonusTileInfo)
            }
        });

        if (cart) {
            trackCart(cart, []);
        }
    });
    /**
     * @description Tracks in dataLayer an information about removing products from minicart
     * @event "minicart.remove.product"
     * @listens "minicart.remove.product"
     */
    eventBus.on('minicart.remove.product', (cart, widget) => {
        if (!widget.removeProductLink) {
            return;
        }

        const tileInfo = widget.removeProductLink.data('analytics');

        if (!tileInfo) {
            return;
        }

        const bonusTileInfo = tileInfo.combinedBonusGtmInfo || [];

        delete tileInfo.combinedBonusGtmInfo;

        tileInfo.quantity = parseInt(widget.removeProductLink.data('quantity'), 10) || 1;

        report('remove_from_cart', {
            ecommerce: {
                currency: pageProperties.currentCurrency,
                value: tileInfo.price,
                feature: 'mini_cart',
                items: [].concat(tileInfo, bonusTileInfo)
            }
        });

        if (cart) {
            trackCart(cart, []);
        }
    });

    /**
     * @description Tracks in dataLayer an information about customer's notifications on a cart page
     * @event "cart.page.notification"
     * @listens "cart.page.notification"
     */
    eventBus.on('cart.page.notification', (widget, msg) => {
        if (widget) {
            const totals = widget.data('totals');

            if (totals) {
                report('ga4_event', {
                    event_name: 'cart_notification',
                    cart_notification: {
                        feature: 'checkout',
                        type: msg,
                        value: totals.grandTotal.value
                    }
                });
            }
        }
    });

    /**
     * @description Tracks in dataLayer an information about adding products to cart express
     * @event "product.added.to.cart.express"
     * @listens "product.added.to.cart.express"
     */
    eventBus.on('product.added.to.cart.express', (checkoutInfo, widget) => {
        const tileInfo = widget.currentGtmInfo || widget.data('analytics');

        if (!tileInfo) {
            return;
        }

        delete tileInfo.pageType;

        tileInfo.quantity ??= 1;

        const addToCartData: TAddToCartData = {
            ecommerce: {
                currency: pageProperties.currentCurrency,
                checkout_type: 'express_pdp',
                value: tileInfo.price,
                feature: window.analyticsPageData.type,
                items: [tileInfo]
            }
        };

        if (Object.prototype.hasOwnProperty.call(tileInfo, 'isBOPISSelected')) {
            addToCartData.ecommerce.bopis = tileInfo.isBOPISSelected ? 'pickup' : 'shipping';
            delete tileInfo.isBOPISSelected;
        }

        report('add_to_cart', addToCartData);

        trackCart(null, [tileInfo]);

        report('view_cart', {
            ecommerce: {
                checkout_step: '01_cart', // eslint-disable-line camelcase
                checkout_type: 'express_pdp', // eslint-disable-line camelcase
                login_status: window.gtmUserData.login_status ? 'member' : 'guest', // eslint-disable-line camelcase
                currency: pageProperties.currentCurrency,
                value: checkoutInfo.config.totals.subTotal.value,
                items: [tileInfo]
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about begin checkout event
     * @event "cart.page.submitted"
     * @listens "cart.page.submitted"
     */
    eventBus.on('cart.page.submitted', (widget, ctaFeature) => {
        if (!widget) {
            return;
        }

        const totals = widget.config ? widget.config.totals : widget.data('totals');
        const items = widget.config ? widget.config.items : widget.data('items');
        const checkoutType = widget.config?.checkoutType || 'regular';
        const discounts = totals.discounts || [];
        const appliedCouponCodes = discounts
            .filter(item => item.isCouponDiscount && item.applied)
            .map(item => item.couponCode)
            .join();

        report('begin_checkout', {
            ecommerce: {
                checkout_step: '01_1_cart',
                checkout_type: checkoutType,
                login_status: window.gtmUserData.login_status ? 'member' : 'guest',
                coupon: appliedCouponCodes,
                currency: pageProperties.currentCurrency,
                value: totals.subTotal.value,
                items: items.map(item => item.gtmInfo),
                ...(ctaFeature ? { feature: ctaFeature } : {})
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about customer's filling a shipping step
     * @event "checkout.shipping.step.activated"
     * @listens "checkout.shipping.step.activated"
     */
    eventBus.on('checkout.shipping.step.activated', checkoutMgr => {
        // omit page refresh
        if (checkoutMgr.currentStep === checkoutMgr.prevStep) {
            return;
        }

        report('page_meta', {
            page: {
                ...window.analyticsPageData,
                id: 'shipping'
            },
            user: window.gtmUserData,
            salesforce_experiments: window.gtmSalesforceExperiments || []
        });
    });

    /**
     * @description Tracks in dataLayer an information about customer's error on a shipping step
     * @event "checkout.shipping.step.error"
     * @listens "checkout.shipping.step.error"
     */
    eventBus.on('checkout.shipping.step.error', checkoutMgr => {
        if (checkoutMgr?.order?.gtmInfo) {
            const totals = checkoutMgr.order.gtmInfo.totals;

            report('ga4_event', {
                event_name: 'shipping_error',
                shipping_error: {
                    feature: 'checkout',
                    checkout_step: '02_1_shipping_error',
                    value: totals.totalPrice
                }
            });
        }
    });

    /**
     * @description Tracks in dataLayer an information about customer's filling a shipping step
     * @event "checkout.shipping.step.submitted"
     * @listens "checkout.shipping.step.submitted"
     */
    eventBus.on('checkout.shipping.step.submitted', checkoutMgr => {
        if (checkoutMgr?.order?.gtmInfo) {
            const totals = checkoutMgr.order.gtmInfo.totals;
            const checkoutType = checkoutMgr.config?.checkoutType || 'regular';

            report('add_shipping_info', {
                ecommerce: {
                    checkout_step: '02_shipping',
                    checkout_type: checkoutType,
                    login_status: window.gtmUserData.login_status ? 'member' : 'guest',
                    coupon: totals.coupon,
                    currency: pageProperties.currentCurrency,
                    value: totals.subtotal,
                    shipping_tier: checkoutMgr.order.gtmInfo.shippingTier,
                    items: checkoutMgr.order.gtmInfo.products
                }
            });
        }
    });

    /**
     * @description Tracks in dataLayer an information about customer's filling a shipping step
     * @event "checkout.billing.step.activated"
     * @listens "checkout.billing.step.activated"
     */
    eventBus.on('checkout.billing.step.activated', checkoutMgr => {
        // omit page refresh
        if (checkoutMgr.currentStep === checkoutMgr.prevStep) {
            return;
        }

        report('page_meta', {
            page: {
                ...window.analyticsPageData,
                id: 'billing'
            },
            user: window.gtmUserData,
            salesforce_experiments: window.gtmSalesforceExperiments || []
        });
    });

    /**
     * @description Tracks in dataLayer an information about customer's error on a billing step
     * @event "checkout.billing.step.error"
     * @listens "checkout.billing.step.error"
     */
    eventBus.on('checkout.billing.step.error', widget => {
        if (widget) {
            const gtmInfo = widget.order?.gtmInfo || widget.data('analytics');

            if (gtmInfo) {
                report('ga4_event', {
                    event_name: 'billing_error',
                    billing_error: {
                        feature: 'checkout',
                        checkout_step: '03_1_billing_error',
                        value: gtmInfo.totals.totalPrice
                    }
                });
            }
        }
    });

    /**
     * @description Tracks in dataLayer an information about customer's filling a billing step
     * @event "checkout.billing.step.submitted"
     * @listens "checkout.billing.step.submitted"
     */
    eventBus.on('checkout.billing.step.submitted', checkoutMgr => {
        if (checkoutMgr?.order?.gtmInfo) {
            const totals = checkoutMgr.order.gtmInfo.totals;
            const paymentMethodId =
                get(checkoutMgr, 'order.billing.payment.selectedPaymentInstruments.0.paymentMethod') ?? '';
            let paymentMethod = get(checkoutMgr, 'order.billing.payment.selectedPaymentInstruments.0.name');
            const checkoutType = checkoutMgr.config?.checkoutType || 'regular';

            if (paymentMethodId.toLowerCase() === 'tabby') {
                paymentMethod = 'Tabby';
            }

            paymentMethod =
                paymentMethod === 'AdyenComponent'
                    ? get(checkoutMgr, 'order.billing.payment.selectedPaymentInstruments.0.selectedAdyenPM')
                    : paymentMethod;

            paymentMethod ??= get(checkoutMgr, 'order.billing.payment.applicablePaymentMethods.0.name') ?? '';

            report('add_payment_info', {
                ecommerce: {
                    checkout_step: '03_billing',
                    checkout_type: checkoutType,
                    login_status: window.gtmUserData.login_status ? 'member' : 'guest',
                    coupon: totals.coupon,
                    currency: pageProperties.currentCurrency,
                    value: totals.subtotal,
                    payment_type: paymentMethod,
                    items: checkoutMgr.order.gtmInfo.products
                }
            });
        }
    });

    /**
     * @description Tracks in dataLayer an information about customer's filling a shipping step
     * @event "checkout.summary.step.activated"
     * @listens "checkout.summary.step.activated"
     */
    eventBus.on('checkout.summary.step.activated', checkoutMgr => {
        // omit page refresh
        if (checkoutMgr.currentStep === checkoutMgr.prevStep) {
            return;
        }

        report('page_meta', {
            page: {
                ...window.analyticsPageData,
                id: 'summary'
            },
            user: window.gtmUserData,
            salesforce_experiments: window.gtmSalesforceExperiments || []
        });

        const totals = checkoutMgr.order.gtmInfo.totals;

        report('ga4_event', {
            event_name: 'review_order',
            review_order: {
                feature: 'checkout',
                checkout_step: '04_order_review',
                value: totals.totalPrice
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about customer's initiate purchase
     * @event "checkout.summary.step.submitted"
     * @listens "checkout.summary.step.submitted"
     */
    eventBus.on('checkout.summary.step.submitted', checkoutMgr => {
        const totals = checkoutMgr.order.gtmInfo.totals;

        report('ga4_event', {
            event_name: 'initiate_purchase',
            initiate_purchase: {
                feature: 'checkout',
                checkout_step: '04_1_order_review',
                value: totals.totalPrice
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about customer's order placement
     * @event "order.placed"
     * @listens "order.placed"
     */
    eventBus.on('order.placed', orderInfo => {
        if (orderInfo) {
            report('purchase', {
                ecommerce: orderInfo
            });
        }
    });

    /**
     * @description Tracks in dataLayer an information about customer's newsletter subscription
     * @event "newsletter.subscribed"
     * @listens "newsletter.subscribed"
     */
    eventBus.on('newsletter.subscribed', subscriptionWidget => {
        const gtmSubscriptionSource = subscriptionWidget?.config?.gtmSubscriptionSource;

        if (gtmSubscriptionSource) {
            report('ga4_event', {
                event_name: 'nl_subscription',
                nl_subscription: {
                    feature: gtmSubscriptionSource
                }
            });
        }
    });

    /**
     * @description Tracks in dataLayer an information about customer's country switch
     * @event "countryselector.submitted"
     * @listens "countryselector.submitted"
     */
    eventBus.on('countryselector.submitted', countrySelectorFormWidget => {
        const localeConfig = countrySelectorFormWidget?.callIfExists('getLocaleConfig');
        const currentCountry = countrySelectorFormWidget?.config?.currentCountry;
        const currentLanguage = countrySelectorFormWidget?.config?.currentLanguage;
        const selectedCountry = localeConfig?.countryCode;
        const selectedLocale = localeConfig?.locale;

        if (!currentCountry || !currentLanguage || !selectedCountry || !selectedLocale) {
            return;
        }

        const refinement = `${currentCountry}_${currentLanguage}~${selectedCountry}_${selectedLocale.split('_')[0]}`;

        if (refinement) {
            report('ga4_event', {
                event_name: 'switch_country',
                switch_country: {
                    feature: 'country_switcher',
                    refinement: refinement
                }
            });
        }
    });

    /**
     * @description Tracks in dataLayer an information about customer's successfull login
     * @event "account.login"
     * @listens "account.login"
     */
    eventBus.on('account.login', ({ location }) => {
        if (location && location.length) {
            report('ga4_event', {
                event_name: 'login',
                login: {
                    feature: 'my_account',
                    method: location
                }
            });
        }
    });

    /**
     * @description Tracks in dataLayer an information about customer's successfull sign up
     * @event "account.signup"
     * @listens "account.signup"
     */
    eventBus.on('account.signup', registerWidget => {
        const location = registerWidget.config?.gtmSignupLocation;

        if (location) {
            report('ga4_event', {
                event_name: 'sign_up',
                sign_up: {
                    feature: 'my_account',
                    method: location
                }
            });
        }
    });

    /**
     * @description Tracks in dataLayer an information about customer's reset password form submission
     * @event "account.resetPassword"
     * @listens "account.resetPassword"
     */
    eventBus.on('account.resetPassword', () => {
        report('ga4_event', {
            event_name: 'reset_password',
            reset_password: {
                feature: 'my_account'
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about customer's account delete form submission
     * @event "account.delete"
     * @listens "account.delete"
     */
    eventBus.on('account.delete', () => {
        report('ga4_event', {
            event_name: 'account_delete',
            account_delete: {
                feature: 'my_account'
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about customer's account update
     * @event "account.update"
     * @listens "account.update"
     */
    eventBus.on('account.update', ({ type }) => {
        if (type && type.length) {
            report('ga4_event', {
                event_name: 'account_update',
                account_update: {
                    feature: 'my_account',
                    type: type
                }
            });
        }
    });

    /**
     * @description Tracks in dataLayer information about the customer's search process
     * @event "search.process"
     * @listens "search.process"
     */
    eventBus.on('search.process', searchTerm => {
        if (searchTerm) {
            report('ga4_event', {
                event_name: 'search',
                search: {
                    feature: 'search',
                    search_term: searchTerm
                }
            });
        }
    });

    /**
     * @description Tracks in dataLayer information about search suggestion click
     * @event "search.suggestion.click"
     * @listens "search.suggestion.click"
     */
    eventBus.on('search.suggestion.click', clickedSuggestionData => {
        const clickedItem = clickedSuggestionData?.clickedItem;
        const searchType = clickedItem?.data('gtmSearchType');

        if (searchType) {
            const itemGTMInfo = clickedItem?.data('analytics');

            report('ga4_event', {
                event_name: 'click_suggestion',
                click_suggestion: {
                    feature: 'search',
                    type: searchType,
                    search_term: clickedSuggestionData.searchTerm || '',
                    item_id: itemGTMInfo?.item_id || ''
                }
            });
        }
    });

    /**
     * @description Tracks in dataLayer information about the customer's FAQ search process
     * @event "faq.search.process"
     * @listens "faq.search.process"
     */
    eventBus.on('faq.search.process', searchTerm => {
        if (searchTerm) {
            report('ga4_event', {
                event_name: 'search',
                search: {
                    feature: 'search_faq',
                    search_term: searchTerm
                }
            });
        }
    });

    /**
     * @description Tracks in dataLayer information about FAQ suggestion click
     * @event "faq.search.suggestion.click"
     * @listens "faq.search.suggestion.click"
     */
    eventBus.on('faq.search.suggestion.click', data => {
        let target = data.targetUrl;

        try {
            const pathname = new URL(data.targetUrl).pathname;

            target = '/' + pathname.split('/').filter(Boolean).pop();
        } catch {
            // keep original targetUrl as fallback
        }

        report('ga4_event', {
            event_name: 'click_suggestion',
            click_suggestion: {
                feature: 'search_faq',
                target,
                search_term: data.searchTerm
            }
        });
    });

    /**
     * @description Tracks in dataLayer view_results or no_result when FAQ search results page loads
     * @event "faq.search.results.view"
     * @listens "faq.search.results.view"
     */
    eventBus.on('faq.search.results.view', data => {
        if (data.noResults) {
            report('ga4_event', {
                event_name: 'no_result',
                no_result: {
                    feature: 'search_faq',
                    search_term: data.searchTerm
                }
            });
        } else {
            report('ga4_event', {
                event_name: 'view_results',
                view_results: {
                    feature: 'search_faq',
                    search_term: data.searchTerm,
                    result: data.resultCount
                }
            });
        }
    });

    /**
     * @description Tracks in dataLayer information about FAQ search result click
     * @event "faq.search.result.click"
     * @listens "faq.search.result.click"
     */
    eventBus.on('faq.search.result.click', data => {
        let target = data.targetUrl;

        try {
            const pathname = new URL(data.targetUrl).pathname;
            const segments = pathname.split('/').filter(Boolean);

            target = segments.length > 1 ? '/' + segments.slice(1).join('/') : pathname;
        } catch {
            // keep original targetUrl as fallback
        }

        report('ga4_event', {
            event_name: 'click_search_result',
            view_results: {
                feature: 'search_faq',
                target,
                search_term: data.searchTerm
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about ship to me click
     * @event "bopis.shipToMe.click"
     * @listens "bopis.shipToMe.click"
     */
    eventBus.on('bopis.shipToMe.click', eventData => {
        report('ga4_event', {
            event_name: 'ship_to_me',
            ship_to_me: {
                feature: 'bopis',
                item_id: eventData.variationGroupId,
                item_variant: eventData.variantId,
                item_price: eventData.price
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about store pickup click
     * @event "bopis.storePickup.click"
     * @listens "bopis.storePickup.click"
     */
    eventBus.on('bopis.storePickup.click', eventData => {
        report('ga4_event', {
            event_name: 'store_pickup',
            store_pickup: {
                feature: 'bopis',
                item_id: eventData.variationGroupId,
                item_variant: eventData.variantId,
                item_price: eventData.price,
                store: eventData.store
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about header login click
     * @event "login.click"
     * @listens "login.click"
     */
    eventBus.on('login.click', url => {
        report('ga4_event', {
            event_name: 'click_navigation',
            click_navigation: {
                feature: 'header',
                element: 'login',
                target: url
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about header cart click
     * @event "cart.click"
     * @listens "cart.click"
     */
    eventBus.on('cart.click', url => {
        report('ga4_event', {
            event_name: 'click_navigation',
            click_navigation: {
                feature: 'header',
                element: 'cart',
                target: url
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about store confirmation
     * @event "bopis.storeConfirmation"
     * @listens "bopis.storeConfirmation"
     */
    eventBus.on('bopis.storeConfirmation', eventData => {
        report('ga4_event', {
            event_name: 'store_confirmation',
            store_confirmation: {
                feature: 'bopis',
                result: eventData.storesCount,
                store: eventData.storeName
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about header wishlist click
     * @event "wishlist.click"
     * @listens "wishlist.click"
     */
    eventBus.on('wishlist.click', eventData => {
        report('ga4_event', {
            event_name: 'click_navigation',
            click_navigation: {
                feature: eventData.feature,
                element: 'wishlist',
                target: '/wishlist'
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about switch to ship to me click
     * @event "bopis.switchToShipping.click"
     * @listens "bopis.switchToShipping.click"
     */
    eventBus.on('bopis.switchToShipping.click', () => {
        report('ga4_event', {
            event_name: 'switch_to_shipping',
            switch_to_shipping: {
                feature: 'bopis'
            }
        });
    });

    /**
     * @description Tracks in dataLayer information PLP filter add event
     * @event "plp.filter.add"
     * @listens "plp.filter.add"
     */
    eventBus.on('plp.filter.add', data => {
        report('ga4_event', {
            event_name: 'filter_add',
            filter_add: {
                feature: 'filter',
                refinement: data.analyticsRefinementLabel || '',
                all_refinements: data.analyticsAllRefinementsLabel || '',
                result: data.resultsCount || 0
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about menu level click
     * @event "menu.level.click"
     * @listens "menu.level.click"
     */
    eventBus.on('menu.level.click', ({ url, tauToEmit, menuLevel }) => {
        report('ga4_event', {
            event_name: 'click_navigation',
            click_navigation: {
                feature: 'main_menu',
                level: 'level_' + menuLevel,
                element: tauToEmit,
                target: url
            }
        });
    });

    /**
     * @description Tracks in dataLayer information PLP filter remove event
     * @event "plp.filter.remove"
     * @listens "plp.filter.remove"
     */
    eventBus.on('plp.filter.remove', data => {
        report('ga4_event', {
            event_name: 'filter_remove',
            filter_remove: {
                feature: 'filter',
                refinement: data.analyticsRefinementLabel || '',
                all_refinements: data.analyticsAllRefinementsLabel || '',
                result: data.resultsCount || 0
            }
        });
    });

    /**
     * @description Tracks in dataLayer information PLP filter remove all event
     * @event "plp.filter.remove.all"
     * @listens "plp.filter.remove.all"
     */
    eventBus.on('plp.filter.remove.all', data => {
        report('ga4_event', {
            event_name: 'filter_remove_all',
            filter_remove_all: {
                feature: 'filter',
                all_refinements: data.analyticsAllRefinementsLabel || '',
                result: data.resultsCount || 0
            }
        });
    });

    /**
     * @description Tracks in dataLayer information PLP sorting change event
     * @event "plp.sorting.change"
     * @listens "plp.sorting.change"
     */
    eventBus.on('plp.sorting.change', data => {
        report('ga4_event', {
            event_name: 'sort_plp',
            sort_plp: {
                feature: 'sorting',
                type: data.selectedSorting || ''
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about main menu content click
     * @event "menu.content.click"
     * @listens "menu.content.click"
     */
    eventBus.on('menu.content.click', (url, categoryId) => {
        report('ga4_event', {
            event_name: 'click_navigation',
            click_navigation: {
                feature: 'main_menu_content',
                category: categoryId,
                target: url
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about main menu content click
     * @event "menu.custom.content.click"
     * @listens "menu.custom.content.click"
     */
    eventBus.on('menu.custom.content.click', (url, alt) => {
        report('ga4_event', {
            event_name: 'click_navigation',
            click_navigation: {
                feature: 'main_menu',
                level: 'level_2',
                element: alt,
                target: url
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about menu models click
     * @event "menu.models.click"
     * @listens "menu.models.click"
     */
    eventBus.on('menu.models.click', (url, categoryId) => {
        report('ga4_event', {
            event_name: 'click_navigation',
            click_navigation: {
                feature: 'main_menu_models',
                category: categoryId,
                target: url
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about hamburger menu categories click
     * @event "menu.categories.click"
     * @listens "menu.categories.click"
     */
    eventBus.on('menu.categories.click', ({ url, element }) => {
        report('ga4_event', {
            event_name: 'click_navigation',
            click_navigation: {
                feature: 'main_menu_birkenstock_categories',
                element: element,
                target: url
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about hamburger menu login/register click
     * @event "menu.login.click"
     * @listens "menu.login.click"
     */
    eventBus.on('menu.login.click', () => {
        report('ga4_event', {
            event_name: 'click_navigation',
            click_navigation: {
                feature: 'main_menu',
                element: 'login',
                target: '/login'
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about hamburger menu footer links click (stores, track order, customer service)
     * @event "menu.footer.click"
     * @listens "menu.footer.click"
     */
    eventBus.on('menu.footer.click', ({ element, target }) => {
        report('ga4_event', {
            event_name: 'click_navigation',
            click_navigation: {
                feature: 'main_menu',
                element: element,
                target: target
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about breadcrumb click
     * @event "breadcrumb.click"
     * @listens "breadcrumb.click"
     */
    eventBus.on('breadcrumb.click', url => {
        report('ga4_event', {
            event_name: 'click_navigation',
            click_navigation: {
                feature: 'breadcrumb',
                target: url
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about footer click
     * @event "footer.click"
     * @listens "footer.click"
     */
    eventBus.on('footer.click', (url, category) => {
        report('ga4_event', {
            event_name: 'click_navigation',
            click_navigation: {
                feature: 'footer',
                category: category,
                target: url
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about refinement category click
     * @event "refinement.category.click"
     * @listens "refinement.category.click"
     */
    eventBus.on('refinement.category.click', ({ category, url }) => {
        report('ga4_event', {
            event_name: 'click_navigation',
            click_navigation: {
                feature: 'filter_bar',
                element: category,
                target: url
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about header links click (stores, logo, back to mainline)
     * @event "header.click"
     * @listens "header.click"
     */
    eventBus.on('header.click', ({ feature, element, url }) => {
        report('ga4_event', {
            event_name: 'click_navigation',
            click_navigation: {
                feature: feature,
                category: element,
                target: url || null
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about product reviews click (yotpo)
     * @event "reviews.click"
     * @listens "header.click"
     */
    eventBus.on('reviews.click', itemID => {
        report('ga4_event', {
            event_name: 'reviews_clicked',
            reviews_clicked: {
                feature: 'product_reviews',
                item_id: itemID || ''
            }
        });
    });

    /**
     * @description Tracks in dataLayer an information about reviews accordion open (yotpo)
     * @event "reviews.accordion.open"
     * @listens "reviews.accordion.open"
     */
    eventBus.on('reviews.accordion.open', itemID => {
        report('ga4_event', {
            event_name: 'accordion_open',
            accordion_open: {
                feature: 'product_reviews',
                item_id: itemID || ''
            }
        });
    });

    /**
     * @description Tracks newsletter popup open event in dataLayer
     * @event "newsletter.popup.open"
     * @listens "newsletter.popup.open"
     */
    eventBus.on('newsletter.popup.open', () => {
        report('ga4_event', {
            event_name: 'nl_popup_visible',
            nl_popup_visible: {
                feature: 'nl_popup'
            }
        });
    });

    /**
     * @description Tracks newsletter popup close event in dataLayer
     * @event "newsletter.popup.close"
     * @listens "newsletter.popup.close
     */
    eventBus.on('newsletter.popup.close', () => {
        report('ga4_event', {
            event_name: 'nl_popup_close',
            nl_popup_close: {
                feature: 'nl_popup'
            }
        });
    });

    /**
     * @description Tracks newsletter popup dismiss event in dataLayer (close the banner by clicking anywhere outside it)
     * @event "newsletter.popup.dismiss"
     * @listens "newsletter.popup.dismiss"
     */
    eventBus.on('newsletter.popup.dismiss', () => {
        report('ga4_event', {
            event_name: 'nl_popup_dismiss',
            nl_popup_dismiss: {
                feature: 'nl_popup'
            }
        });
    });
}
